/**
 * CR-5275: TOTP MFA enrolment wizard.
 *
 * Reached from /login when the backend responds with
 * `mfa_setup_required: true` — the password was valid but the user hasn't
 * registered an authenticator app yet. We pull the provisioning URI from
 * the backend, render it as a QR + plain-text fallback, accept the first
 * code the user types, and on success finalize the login session with
 * the real DRF auth token returned from the confirm endpoint.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

interface SetupPayload {
  provisioning_uri: string;
  secret_base32: string;
  issuer: string;
  account_label: string;
  mfa_session_token: string;
}

const MfaSetup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { finalizeMfaLogin } = useAuth();
  const { branding } = useBranding();

  // Session token comes from the previous /login response, carried in
  // navigate(..., { state: { sessionToken } }). If somebody lands here
  // directly we send them back to /login.
  const initialSessionToken = (location.state as { sessionToken?: string } | null)?.sessionToken;
  const [sessionToken, setSessionToken] = useState<string | undefined>(initialSessionToken);
  const [setup, setSetup] = useState<SetupPayload | null>(null);
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [confirming, setConfirming] = useState<boolean>(false);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authAPI.mfaSetupStart(sessionToken);
        if (cancelled) return;
        const data = res.data.data as SetupPayload;
        setSetup(data);
        // Backend refreshes the session token on each setup-start so the
        // user gets a fresh TTL window to type the confirm code.
        setSessionToken(data.mfa_session_token);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        setError(axiosErr.response?.data?.detail || 'Could not start MFA setup. Try logging in again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionToken) return;
    setError('');
    setConfirming(true);
    try {
      const res = await authAPI.mfaSetupConfirm(sessionToken, code.trim());
      const { user, token } = res.data.data as { user: import('../lib/api').User; token: string };
      finalizeMfaLogin(user, token);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Could not confirm code. Try again.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: branding.backgroundColor }}
    >
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Set up two-factor auth</h1>
            <p className="text-gray-600 mt-2 text-sm">
              Your account requires MFA. Scan the code below with Google Authenticator,
              Authy, or another TOTP app, then enter the 6-digit code it shows you.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          )}

          {!loading && setup && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-white border rounded">
                  <QRCodeSVG value={setup.provisioning_uri} size={192} />
                </div>
              </div>
              <details className="mb-6">
                <summary className="text-sm text-gray-600 cursor-pointer">
                  Can't scan? Enter this secret manually
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded text-xs font-mono break-all">
                  {setup.secret_base32}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Account: <span className="font-medium">{setup.account_label}</span>
                  {' · '}Issuer: <span className="font-medium">{setup.issuer}</span>
                </p>
              </details>

              <form onSubmit={handleConfirm} className="space-y-4">
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-1">
                    Enter 6-digit code from your authenticator app
                  </label>
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center tracking-widest text-lg"
                    placeholder="000000"
                    disabled={confirming}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={confirming || code.length !== 6}
                  className="w-full py-3 px-4 rounded-lg text-white font-medium disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ backgroundColor: branding.buttonColor }}
                >
                  {confirming ? 'Verifying…' : 'Confirm and sign in'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-500">
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaSetup;
